/**
 * RUEDA-CONFIG-FACTORY — objeto de configuración de rueda SIN estado global.
 *
 * `crearConfiguracionDeRuedaParaTipo(tipoDeRueda)` devuelve un objeto inmutable
 * con los datos y los métodos money-path de un tipo de rueda concreto, cerrando
 * sobre la config de ESE tipo. No lee ni escribe el estado global
 * `_activeConfig` de `roulette-numbers-order-config.js`.
 *
 * Por qué existe: hoy el money-path (`lotowheel-draw-engine`,
 * `lotowheel-contabilidad`, resolvers) consulta el SSOT vía getters globales
 * que dependen de `setActiveType()`. N sorteos comparten ese global. Una
 * configuración de rueda local por sorteo elimina la dependencia del estado
 * compartido: dos sorteos de tipo distinto liquidando en paralelo no pueden
 * pisarse — no hay nada compartido. Ver `docs/MODELO_RECETA_AGNOSTICA.md`.
 *
 * ESTADO: el money-path del draw-engine + contabilidad YA consume esta factory
 * (PR-1b). Los getters globales del SSOT siguen vivos en paralelo porque
 * apuestas y resolvers todavía los leen — se migran en PR-1c, y ahí se borra la
 * duplicación con el SSOT. Tocar esta factory impacta el cálculo de premios de
 * cada sorteo: está en el critical path.
 *
 * Equivalencia con el SSOT: el `test-rueda-golden-master.js` prueba que
 * `evaluarSiApuestaCubreElSectorGanador` / `obtenerSectoresCubiertosPorApuesta` /
 * `obtenerMultiplicadorDePremioDeApuesta` de esta factory dan EXACTAMENTE el
 * mismo resultado que los getters globales, para los 7 tipos de rueda.
 *
 * Nomenclatura: el dominio es "rueda" (Rueda de la Fortuna) — NUNCA "ruleta"
 * (término de casino). Nombres largos y auto-descriptivos: el nombre dice qué
 * hace, sobre qué y cómo, sin necesidad de comentario aparte.
 */

// `shared/` es un paquete ESM (`"type": "module"`). El SSOT exporta named.
// `ROULETTE_TYPES` ya pre-construye las 7 configs al import — son stateless,
// no dependen de `setActiveType`. La factory las envuelve.
//
// DEPENDENCIA DE RUNTIME (documentado 2026-07-10, no arreglado — deuda menor):
// el backend (CommonJS) consume este archivo vía `require()` directo, lo cual
// solo funciona porque Node ≥20.19 soporta require(esm) como feature
// experimental default-on (verificado: Brasil Node v20.20.2, Perú v22.21.1,
// ambos con --experimental-require-module=true). Con Node <20.19 este
// require() falla con "require() of ES Module ... not supported" — el
// backend NO arrancaría (fail-fast al cargar lotowheel-draw-engine.js, no un
// bug silencioso). Fix robusto pendiente: exportar también vía
// `module.exports` (dual CJS/ESM) o migrar el consumidor a `import()`
// dinámico. No urgente mientras el mínimo de Node en prod sea ≥20.19 — pero
// verificar la versión de Node ANTES de bootstrapear cualquier server nuevo.
import {
    ROULETTE_TYPES as tablaDeConfiguracionesPorTipoDeRueda,
    ZERO_DISPLAY_MODES,
    DEFAULT_ZERO_LABELS,
    normalizarClaveDeTipoDeRueda,
} from './roulette-numbers-order-config.js';

// Cache de objetos de configuración de rueda ya construidos, indexado por tipo.
// Las configs son inmutables y `execute()` invoca la factory cada ~60s × N
// sorteos — cachear evita reconstruir el objeto en cada sorteo. Clave = tipo
// de rueda en mayúsculas.
const cacheDeConfiguracionesDeRuedaPorTipo = new Map();

// Los tipos de apuesta directa y de cobertura ya NO viven hardcodeados aqui.
// La factory los lee directo de la receta agnostica via los indices
// `apuestaFijaPorIdDeReceta` + `apuestaGeometricaPorIdDeReceta` construidos
// dentro de `construirConfiguracionDeRuedaDesde`.

// ─── Modelo agnostico ──────────────────────────────────────────────
//
// El sector lleva su `paleta` declarada en la receta (clave agnostica en
// theme.paletas: 'paleta-0', 'paleta-1', 'paleta-2', ...). El metodo
// `obtenerClaveDePaletaDeSector(sector)` devuelve esa clave; el caller la
// usa directo como `data-paleta="<clave>"` en el HTML — el CSS pinta via
// vars del tema (apply-theme-css-vars expone --paleta-N-base/light/dark/soft).

// `exigirTipoDeApuestaConocidoEnLaRueda` se define como closure dentro de
// `construirConfiguracionDeRuedaDesde` para tener acceso a los indices
// agnosticos (`apuestaFijaPorIdDeReceta` + `apuestaGeometricaPorIdDeReceta`)
// armados desde la receta. Cero estado global, validacion fail-fast contra la
// receta agnostica (no contra `configDeRueda.bets`).

/**
 * Crea (o devuelve cacheado) el objeto de configuración money-path de un tipo
 * de rueda, buscándolo en la tabla local de tipos `ROULETTE_TYPES`.
 *
 * Es el camino que usan backend y los fronts mono-sorteo: tienen la tabla
 * bundleada y resuelven el tipo localmente. El camino alternativo —
 * `construirConfiguracionDeRuedaDesde` con la config traída del endpoint
 * `wheel-data`— usa la misma construcción, solo cambia el origen de los datos.
 *
 * @param {string} tipoDeRueda - id del tipo ('european', 'rueda10_american', ...).
 * @returns {Object} objeto congelado con datos + métodos money-path.
 * @throws {Error} si el tipo no existe — fail-fast (un tipo inválido = código
 *   viejo o config en DB corrupta; silenciarlo daría premios mal calculados).
 */
export function crearConfiguracionDeRuedaParaTipo(tipoDeRueda) {
    const claveCanonicaDeTipo = normalizarClaveDeTipoDeRueda(tipoDeRueda);
    if (cacheDeConfiguracionesDeRuedaPorTipo.has(claveCanonicaDeTipo)) {
        return cacheDeConfiguracionesDeRuedaPorTipo.get(claveCanonicaDeTipo);
    }

    // FUENTE money-path: leer la config materializada desde
    // `tablaDeConfiguracionesPorTipoDeRueda` (ROULETTE_TYPES) en lugar de
    // re-derivar de la receta. Eso garantiza que las propiedades planas que
    // el factory expone como referencias (`matGridDeLaRueda`,
    // `ordenDeSectoresEnLaRueda`, etc.) compartan la MISMA instancia que el
    // SSOT — sin duplicar materializaciones. Si el tipo no existe en la tabla
    // (que se construye desde RECETAS_AGNOSTICAS al import), fail-fast.
    const configDeRueda = tablaDeConfiguracionesPorTipoDeRueda[claveCanonicaDeTipo];
    if (!configDeRueda) {
        throw new Error(
            `[ConfiguracionDeRueda] Tipo de rueda desconocido: "${tipoDeRueda}". ` +
                `Válidos: ${Object.keys(tablaDeConfiguracionesPorTipoDeRueda).join(', ')}. ` +
                'Probable causa: proceso con código viejo (restart pendiente) o config en DB corrupta.'
        );
    }

    const configuracionDeRueda = construirConfiguracionDeRuedaDesde(configDeRueda);
    cacheDeConfiguracionesDeRuedaPorTipo.set(claveCanonicaDeTipo, configuracionDeRueda);
    return configuracionDeRueda;
}

/**
 * Construye el objeto de configuración money-path de una rueda a partir de su
 * config cruda — el objeto con `id`, `order`, `reds`, `bets`, `matGrid`, etc.
 *
 * Esta es la lógica de construcción REAL; `crearConfiguracionDeRuedaParaTipo`
 * la alimenta con la tabla local, y el cliente del endpoint `wheel-data` la
 * alimenta con el JSON que devuelve el backend. Una sola lógica money-path,
 * dos orígenes de datos — el front mono-sorteo usa la tabla bundleada, un
 * cliente data-driven usa el endpoint. Idéntico resultado para idéntica config.
 *
 * El objeto es inmutable y autosuficiente: cada método cierra sobre la config
 * recibida, sin tocar estado global.
 *
 * @param {Object} configDeRueda - config cruda del tipo de rueda (forma de
 *   `ROULETTE_TYPES[tipo]` / del endpoint `wheel-data`).
 * @returns {Object} objeto congelado con datos + métodos money-path.
 */
export function construirConfiguracionDeRuedaDesde(configDeRueda) {
    // sectoresUniformesDeLaRueda — proyección rica del shape de cada sector que
    // emite el puente SSOT (PR-SEMILLA del MODELO_SECTOR_UNIVERSAL). Cada
    // entrada lleva `id`, `valor` (identidad money-path), `esCero` (identidad
    // de pago), 3 capas visuales `top`/`bot`/`grad`, `color` ('rojo'/'negro'
    // para no-ceros), `peso` + arcs. Centralizar la lectura aquí elimina la
    // divergencia "branch zeros + branch reds" que existía en cada método.
    //
    // Una sola lectura del shape rico, varios métodos derivados — el día que
    // llegue una rueda no-numérica (animales/equipos), estos métodos ya
    // funcionan sin tocar nada porque razonan sobre `valor`/`esCero`/`color`
    // del shape uniforme, no sobre branches por tipo.
    //
    // Fail-fast: shared/ es frontera pública (CLAUDE.md §2A). Si llega un
    // corrupto), lanzamos en construcción de la factory — antes de que un
    // sorteo pague algo con datos incompletos. El golden-master + las 7
    // recetas reales SIEMPRE tienen el campo; un fallo aquí indica drift.
    const sectoresUniformesDeLaRueda = configDeRueda.sectoresUniformesDeLaRueda;
    if (!Array.isArray(sectoresUniformesDeLaRueda)) {
        throw new Error(
            `[ConfiguracionDeRueda] El config de tipo "${configDeRueda.id || '<sin id>'}" no trae ` +
                `\`sectoresUniformesDeLaRueda\`. Causa probable: deploy parcial (backend nuevo vs ` +
                `shared viejo) o JSON corrupto del endpoint wheel-data. ` +
                `Money-path: lanzamos al construir la factory para evitar que un sorteo pague ` +
                `con shape incompleto. Reconstruir ROULETTE_TYPES o purgar caché del cliente.`
        );
    }

    // Índice por valor para lookup O(1) — la factory se llama una vez por
    // tipo (con caché por clave) y se reusa en cada llamada money-path. El
    // costo del Map se amortiza desde la primera consulta. `Object.freeze`
    // del Map garantiza inmutabilidad estricta del estado interno; un futuro
    // método de la factory que intente `sectorUniformePorValor.set(...)` en
    // strict mode lanza, atrapando el bug en desarrollo.
    const sectorUniformePorValor = new Map();
    for (const sectorUniforme of sectoresUniformesDeLaRueda) {
        sectorUniformePorValor.set(sectorUniforme.valor, sectorUniforme);
    }
    Object.freeze(sectorUniformePorValor);

    // Índice por VALOR del sector → posición entre los SECTORES DE CERO de la
    // rueda (0 = primer cero, 1 = segundo, etc). Usado por
    // `obtenerDisplayDeSector*` para los modos POR_CEROS (cuántas veces
    // repetir "0") y POR_TEXTO (cuál label de DEFAULT_ZERO_LABELS).
    //
    // Pre-computado al construir la factory desde `sectoresUniformesDeLaRueda`.
    // Da O(1) por consulta. Una rueda sin sectores fuera de grilla → Map vacío
    // → los métodos saltan a `return sector` sin modificarlo, coherente con
    // sectores de grilla.
    const indiceDeCeroEntreSectoresDeCeroPorValor = new Map();
    let contadorDeCeros = 0;
    for (const sectorUniforme of sectoresUniformesDeLaRueda) {
        if (sectorUniforme.esCero === true) {
            indiceDeCeroEntreSectoresDeCeroPorValor.set(sectorUniforme.valor, contadorDeCeros);
            contadorDeCeros++;
        }
    }
    Object.freeze(indiceDeCeroEntreSectoresDeCeroPorValor);

    // ─── Indice money-path de apuestas FIJAS leido de la receta ─────────────
    //
    // Money-path interno consulta este indice para resolver cobertura +
    // multiplicador de apuestas con `sectores` fijos. El indice se arma UNA
    // vez al construir la factory desde `configDeRueda.apuestas` (la lista
    // agnostica de `recetas-agnosticas.js`).
    //
    // Clave UNICA = el id de la receta (agnostico). Los emisores (mat web,
    // POS Java, backend) hablan el id agnostico. Si la receta declara
    // `'frutales'`/`'mamiferos'`/etc. como apuesta fija, basta con agregarla
    // a la receta y los emisores envian ese id — el indice la recoge sin
    // tocar este archivo.
    //
    // Apuestas GEOMETRICAS (`aciertoDirecto`/`duo`/`linea`/`cuadra`/`dobleLinea`
    // declaradas con `aridad` en lugar de `sectores`) NO entran al indice: su
    // cobertura viaja en el `valor` del cliente (`'5-8-12'`). El metodo
    // money-path las detecta y parsea por separado.
    const apuestasDeLaReceta = Array.isArray(configDeRueda.apuestas) ? configDeRueda.apuestas : [];
    const apuestaFijaPorIdDeReceta = new Map();
    for (let i = 0; i < apuestasDeLaReceta.length; i++) {
        const apuesta = apuestasDeLaReceta[i];
        if (apuesta.activa === false) continue;
        if (!Array.isArray(apuesta.sectores)) continue;
        apuestaFijaPorIdDeReceta.set(apuesta.id, {
            sectores: apuesta.sectores,
            multiplicador: apuesta.multiplicador,
            grupo: apuesta.grupo,
            idReceta: apuesta.id,
        });
    }
    Object.freeze(apuestaFijaPorIdDeReceta);

    // Indice de apuestas GEOMETRICAS por id de receta. Same Map para acelerar
    // lookups de existencia + multiplicador. El `valor` se parsea on-demand.
    const apuestaGeometricaPorIdDeReceta = new Map();
    for (let i = 0; i < apuestasDeLaReceta.length; i++) {
        const apuesta = apuestasDeLaReceta[i];
        if (apuesta.activa === false) continue;
        if (typeof apuesta.aridad !== 'number') continue;
        apuestaGeometricaPorIdDeReceta.set(apuesta.id, {
            aridad: apuesta.aridad,
            multiplicador: apuesta.multiplicador,
            grupo: apuesta.grupo,
            idReceta: apuesta.id,
        });
    }
    Object.freeze(apuestaGeometricaPorIdDeReceta);

    // Indice agnostico: id de receta -> entry cruda de la receta agnostica.
    // Lo consumen helpers UI (front-customer/TV) que necesitan saber el shape
    // de una apuesta (aridad/sectores/multiplicador/grupo) sin razonar por
    // convencion de nombre. Proyeccion fiel del array `apuestas` indexado por id.
    const apuestaCrudaPorIdDeReceta = new Map();
    for (let i = 0; i < apuestasDeLaReceta.length; i++) {
        const apuesta = apuestasDeLaReceta[i];
        if (apuesta.activa === false) continue;
        apuestaCrudaPorIdDeReceta.set(apuesta.id, apuesta);
    }
    Object.freeze(apuestaCrudaPorIdDeReceta);

    /**
     * Fail-fast money-path: valida que `idDeApuesta` es conocido para esta
     * rueda. Solo acepta ids agnosticos declarados en la receta. Cualquier
     * otro id es dato corrupto — no se liquida en silencio.
     */
    function exigirTipoDeApuestaConocidoEnLaRueda(idDeApuesta) {
        if (apuestaFijaPorIdDeReceta.has(idDeApuesta)) return;
        if (apuestaGeometricaPorIdDeReceta.has(idDeApuesta)) return;
        const idsValidos = [...apuestaFijaPorIdDeReceta.keys(), ...apuestaGeometricaPorIdDeReceta.keys()];
        throw new Error(
            `[ConfiguracionDeRueda] Tipo de apuesta desconocido: "${idDeApuesta}" para rueda ` +
                `"${configDeRueda.id}". Money-path: una apuesta con tipo no reconocido es dato corrupto — ` +
                `no se liquida en silencio. Validos para esta rueda: ${idsValidos.join(', ')}.`
        );
    }

    /**
     * Pre-check puro (sin throw) del id de apuesta. Permite al resolver
     * aislar tipos desconocidos sin matar el draw. El fail-fast (throw)
     * sigue activo para callers que deben detener todo.
     * @returns {boolean}
     */
    function tieneTipoDeApuestaConocidoEnLaRueda(idDeApuesta) {
        return apuestaFijaPorIdDeReceta.has(idDeApuesta) || apuestaGeometricaPorIdDeReceta.has(idDeApuesta);
    }

    // SSOT de grupos materializados — el mapa que emite el puente, espejo
    // 1:1 de `rueda.grupos` del motor. Para LOTO trae rojos/negros/pares/
    // impares/bajos/altos/gemelos/docenaN/columnaN/bloqueX. Para una rueda
    // de animalitos traerá lo que declare la receta (mamiferos/aves/etc.).
    // GETTERS delegados a este mapa — un solo objeto, una sola SSOT.
    const gruposDeLaRueda = configDeRueda.gruposDeLaRueda || {};

    const configuracionDeRueda = {
        // --- Datos del tipo (lectura directa de la config inmutable) ---
        //
        // Cada propiedad es un alias estable al mismo objeto del config crudo
        // — los callers ya no necesitan navegar `configDeTipoDeRueda.X`, que
        // era una friction repetida en 8 archivos (§8.10b del MODELO_SECTOR_
        // UNIVERSAL). El nombre largo en castellano sigue el patrón del
        // archivo (`cantidadTotalDeSectoresDeLaRueda`, `sectoresDeCeroDeLa-
        // Rueda`, etc.) — auto-descriptivo, money-path narrado en su contexto.
        //
        // `configDeTipoDeRueda` se MANTIENE por retrocompat con callers que
        // todavía navegan el path largo (`getActiveType` del mat factory de
        // shared, §8.2 cierra). Cuando todos migren, se puede borrar.
        tipoDeRueda: configDeRueda.id,
        configDeTipoDeRueda: configDeRueda,
        cantidadTotalDeSectoresDeLaRueda: configDeRueda.totalNumbers,
        ordenDeSectoresEnLaRueda: configDeRueda.order,
        // Shape rico universal — array de sectores con
        // {valor, esCero, top, bot, grad, paleta, peso, arcGrados, arcInicio,
        //  arcFin, props}. Lo consume el wheel renderer (§8.1, `_buildSegments`),
        // mat (filtrando por esCero), apply-zero-display-to-mat-dom y cualquier
        // consumer que necesite geometria no-uniforme o capas visuales
        // — los callers ahora hacen .filter(s => s.esCero === true).
        sectoresUniformesDeLaRueda: sectoresUniformesDeLaRueda,
        matGridDeLaRueda: configDeRueda.matGrid,

        // --- GRUPOS ---
        // gruposDeLaRueda: mapa interno {grupoSemantico: [valores]} que el
        // bridge construye para uso interno de la factory (cobertura money-path).
        // sectoresParesDeLaRueda, ..., bloquesDeLaRueda, paresEspejoDeLaRueda,
        // leen apuestasPorId del modelo agnostico directamente, o iteran
        // sectoresUniformesDeLaRueda filtrando por esCero.
        gruposDeLaRueda,

        // Geometría visual (radios/proporciones de la rueda para canvas). No es
        // sub-objeto de `matGrid` — `matGrid` describe la grilla del mat web/POS;
        // `geometry` describe la rueda misma.
        geometriaDeLaRueda: configDeRueda.geometry || null,
        // La receta agnóstica cruda (array de apuestas) — la lee i18n tWithConfig
        // para derivar rangos (docenas/bajos/altos) de los `sectores` de cada una.
        apuestas: configDeRueda.apuestas || [],

        /**
         * Lookup agnostico de la receta: devuelve la entry cruda de una
         * apuesta por su id, o `null` si no existe. La entry incluye
         * `{id, grupo, multiplicador, sectores?, aridad?, categoria,
         * canales?, autorizable, activa, color?}` — todo lo que la receta
         * declara. Es la fuente unica de verdad agnostica para callers
         * UI que necesitan saber el shape de una apuesta SIN razonar por
         * convencion de nombre (`bet.type === 'aciertoDirecto'` etc.).
         *
         * Ejemplo: si `apuesta.aridad === 1`, es una apuesta de cobertura
         * de un solo sector — la UI puede tratarla genericamente sin
         * hardcodear el id.
         *
         * con ids de receta exactos. Para resolver cobertura money-path
         * con sinonimos, usar los metodos especializados de la factory.
         */
        obtenerApuestaPorId(idDeApuesta) {
            return apuestaCrudaPorIdDeReceta.get(idDeApuesta) || null;
        },

        // --- Métodos money-path (cierran sobre `configDeRueda`, sin global) ---
        //
        // "Sector" = el item sorteable de la rueda. Hoy todos los sectores son
        // números (la rueda es numérica), así que el valor de un sector es un
        // entero — por eso adentro se hace aritmética de enteros. El nombre
        // `sector` (no `numero`) es deliberado: el modelo universal (TRABAJO A)
        // contempla sectores no-numéricos; el día que existan, estos nombres
        // ya son correctos sin tocar nada.

        /** ¿El sector es uno de los ceros de este tipo de rueda? */
        evaluarSiSectorEsSectorDeCero(sector) {
            const sectorUniforme = sectorUniformePorValor.get(sector);
            return sectorUniforme !== undefined && sectorUniforme.esCero === true;
        },

        /**
         * Posición (índice) de un sector dentro del orden de la rueda de este
         * tipo. Devuelve -1 si el sector no existe en la rueda. El `id` del
         * sector uniforme ES su posición en el orden.
         */
        obtenerPosicionDeSectorEnElOrdenDeLaRueda(sector) {
            const sectorUniforme = sectorUniformePorValor.get(sector);
            return sectorUniforme !== undefined ? sectorUniforme.id : -1;
        },

        /**
         * Clave de paleta agnostica del sector — espejo del POS Java
         * `WheelData.paletaPorNumero.get(numero)`. La receta declara
         * {@code sector.paleta} (clave en theme.paletas) y este getter lo
         * que devolvia CSS class string 'red'/'black'/'green' — ahora
         * devuelve la clave agnostica y el caller resuelve hex contra el
         * tema activo via {@code theme.paletas[clave].base/dark/light}.
         *
         * <p>Devuelve null si el sector no existe en la rueda.
         */
        obtenerClaveDePaletaDeSector(sector) {
            const sectorUniforme = sectorUniformePorValor.get(sector);
            return sectorUniforme && sectorUniforme.paleta ? sectorUniforme.paleta : null;
        },

        /**
         * Multiplicador de premio de una apuesta. Money-path LEE de la receta
         * agnostica directo. Cada apuesta declara su multiplicador propio —
         * cero overrides por sector. Si dos sectores pagan distinto al mismo
         * tipo de cobertura, son dos apuestas distintas en la receta.
         *
         * El `idDeApuesta` debe ser un id declarado en la receta. Cualquier
         * otro id es dato corrupto: fail-fast.
         */
        /**
         * Pre-check sin throw (espejo del fail-fast). Permite al resolver pre-
         * validar y aislar apuestas con tipo desconocido sin matar el draw.
         */
        tieneTipoConocido(idDeApuesta) {
            return tieneTipoDeApuestaConocidoEnLaRueda(idDeApuesta);
        },

        obtenerMultiplicadorDePremioDeApuesta(idDeApuesta) {
            exigirTipoDeApuestaConocidoEnLaRueda(idDeApuesta);
            const apuesta =
                apuestaFijaPorIdDeReceta.get(idDeApuesta) || apuestaGeometricaPorIdDeReceta.get(idDeApuesta);
            const m = apuesta.multiplicador;
            if (typeof m !== 'number' || !Number.isFinite(m) || m <= 0) {
                throw new Error(
                    `[ConfiguracionDeRueda] Multiplicador de premio invalido para la apuesta "${idDeApuesta}" ` +
                        `en la rueda "${configDeRueda.id}": ${JSON.stringify(m)}. ` +
                        'Money-path: una apuesta sin multiplicador valido es config corrupta — se detiene la ' +
                        'liquidacion, NO se paga 0 a un ganador en silencio.'
                );
            }
            return m;
        },

        /**
         * ¿La apuesta es DIRECTA al número (pleno / grupo 'directo')? El multiplicador
         * del lightning aplica SOLO al pleno (como Evolution/Pragmatic): las apuestas
         * externas (color/par/docena) pagan tabla normal aunque cubran el número
         * afortunado. Money-path: clasificar mal = pagar el mult a externas = sobrepago.
         * @param {string} idDeApuesta
         * @returns {boolean} true si el grupo de la apuesta es 'directo'
         */
        esApuestaDirectaAlNumero(idDeApuesta) {
            const apuesta =
                apuestaFijaPorIdDeReceta.get(idDeApuesta) || apuestaGeometricaPorIdDeReceta.get(idDeApuesta);
            return !!apuesta && apuesta.grupo === 'directo';
        },

        /**
         * ¿La apuesta (id + valor) cubre el sector ganador del sorteo? Decide
         * si el ticket gana.
         *
         * - Apuestas FIJAS (con `sectores` declarados en la receta —
         *   `rojos`/`docena1`/`bloqueA`/`gemelos`/`espejo_X_Y`/`loto`/`argentina`/...):
         *   el `valor` se ignora; lee `apuesta.sectores`.
         * - Apuestas GEOMETRICAS (con `aridad` — `aciertoDirecto`/`duo`/
         *   `linea`/`cuadra`/`dobleLinea`): parsea el `valor` que envia el
         *   cliente (string con sectores unidos por '-').
         */
        evaluarSiApuestaCubreElSectorGanador(tipoDeApuesta, valorDeApuesta, sectorGanadorDelSorteo) {
            exigirTipoDeApuestaConocidoEnLaRueda(tipoDeApuesta);

            const apuestaFija = apuestaFijaPorIdDeReceta.get(tipoDeApuesta);
            if (apuestaFija) return apuestaFija.sectores.includes(sectorGanadorDelSorteo);

            // Geometrica: el cliente arma cobertura, el `valor` viene como
            // string con sectores unidos por '-' (ej. "5" para aridad 1,
            // "5-8" para aridad 2, "1-2-3" para aridad 3). split+map(Number)
            // funciona uniformemente para todas las aridades.
            return String(valorDeApuesta).split('-').map(Number).includes(sectorGanadorDelSorteo);
        },

        /**
         * Array de sectores que cubre una apuesta (id + valor). Misma logica
         * que `evaluarSiApuestaCubreElSectorGanador` pero devuelve el set
         * completo en lugar de evaluar un sector puntual.
         */
        obtenerSectoresCubiertosPorApuesta(tipoDeApuesta, valorDeApuesta) {
            exigirTipoDeApuestaConocidoEnLaRueda(tipoDeApuesta);

            const apuestaFija = apuestaFijaPorIdDeReceta.get(tipoDeApuesta);
            if (apuestaFija) return apuestaFija.sectores;

            // Geometrica: `valor` con sectores unidos por '-' (ver doc en
            // `evaluarSiApuestaCubreElSectorGanador`).
            return String(valorDeApuesta).split('-').map(Number);
        },

        /**
         * Letra del bloque que contiene a un sector ('A', 'B'...), o null si el
         * sector es un cero, un sector desconocido, o la rueda no tiene
         * bloques.
         *
         * grupo === 'bloques' y busca el sector en cada apuesta.sectores. La
         * letra se extrae del id ('bloqueA' → 'A', 'bloqueB' → 'B'). Cero
         */
        obtenerBloqueDeSector(sector) {
            const sectorUniforme = sectorUniformePorValor.get(sector);
            if (sectorUniforme === undefined) return null;
            if (sectorUniforme.esCero === true) return null;
            for (const apuesta of apuestasDeLaReceta) {
                if (apuesta.grupo !== 'bloques') continue;
                if (!Array.isArray(apuesta.sectores)) continue;
                if (apuesta.sectores.includes(sector)) {
                    // Convencion: el id es "bloque<LETRA>". La letra es lo que
                    // sigue al prefijo. Si la receta usara otro esquema (animales,
                    // equipos), devolveriamos el id entero o un campo declarado
                    // en la receta. Por ahora el unico esquema vivo es bloque<L>.
                    const prefijo = 'bloque';
                    if (apuesta.id.startsWith(prefijo)) return apuesta.id.slice(prefijo.length);
                    return apuesta.id;
                }
            }
            return null;
        },

        /**
         * Valor de display de un sector según el modo de presentación del cero
         * configurado en el sorteo. Equivale a `getDisplayNumber(num, modo,
         * labels)` del SSOT, con la config explícita.
         *
         * Solo afecta a los sectores de cero — un sector normal (1..N) se
         * devuelve tal cual. En POR_TEXTO concatena top + bot en un string
         * único ("SORTE 37"): contextos no-wheel (tiquete, historial) muestran
         * el cero en una línea. La variante `...Compacto` da solo el número
         * corto, para celdas donde no cabe el texto completo.
         *
         * @returns {number|string} En POR_NUMERO devuelve `bot` del sector
         *   uniforme — puede ser `number` (european 37, american 37/38,
         *   triple_zero 37/38/39) o `string` (mini `'12+1'`, mini18 `'18+1'`,
         *   rueda10 `'10+1'`, rueda10_american `'10+1'`/`'10+2'`). Sector
         *   no-cero se devuelve tal cual (typeof depende del input del caller).
         */
        obtenerDisplayDeSector(sector, modo = ZERO_DISPLAY_MODES.POR_NUMERO, labels) {
            // Lee desde `sectoresUniformesDeLaRueda` vía dos mapas pre-
            // computados O(1) (§8.6): `sectorUniformePorValor` para el
            // display value (bot), `indiceDeCeroEntreSectoresDeCeroPorValor`
            // para la posición del cero entre los ceros (POR_CEROS y
            // POR_TEXTO). Antes este método hacía `configDeRueda.zeros.find`
            // + `indexOf` — O(n) lineal y ataba el método al shape disperso
            // (`zeros: [{value, displayValue, label}]`), incompatible con
            // ruedas universales sin sectores de cero numéricos.
            const sectorUniforme = sectorUniformePorValor.get(sector);
            if (!sectorUniforme || sectorUniforme.esCero !== true) return sector;
            // Fail-fast (§2A): sector marcado esCero=true DEBE traer `bot`.
            // Devolver `undefined` silencioso rompería el caller
            // (`displayValueForTicket` imprime `String(undefined) = "undefined"`
            // en el ticket). Una receta con sector de cero sin displayValue
            // es config corrupta — el motor lo detectaría en construcción
            // del puente, pero la defensa acá ancla el contrato del método.
            if (sectorUniforme.bot == null) {
                throw new Error(
                    `[ConfiguracionDeRueda] Sector ${sector} de la rueda ` +
                        `"${configDeRueda.id}" está marcado esCero=true pero no declara ` +
                        '`bot` (displayValue). Receta corrupta — un sector de cero sin valor ' +
                        'de display rompería tickets/UI.'
                );
            }
            const indiceDelCero = indiceDeCeroEntreSectoresDeCeroPorValor.get(sector);

            if (modo === ZERO_DISPLAY_MODES.POR_CEROS) {
                return '0'.repeat(indiceDelCero + 1);
            }
            if (modo === ZERO_DISPLAY_MODES.POR_TEXTO) {
                const override = Array.isArray(labels) ? labels[indiceDelCero] : null;
                // labels: objeto {top, bot} → se concatenan; string → se usa
                // tal cual; ausente → defaults DEFAULT_ZERO_LABELS.
                if (override && typeof override === 'object' && !Array.isArray(override)) {
                    const textoArriba = override.top != null ? String(override.top).trim() : '';
                    const textoAbajo = override.bot != null ? String(override.bot).trim() : '';
                    const combinado = [textoArriba, textoAbajo].filter(Boolean).join(' ');
                    if (combinado) return combinado;
                } else if (typeof override === 'string' && override.trim() !== '') {
                    return override.trim();
                }
                return DEFAULT_ZERO_LABELS[indiceDelCero] || DEFAULT_ZERO_LABELS[0];
            }
            // POR_NUMERO (default): `bot` del sector uniforme = displayValue
            // del tipo (37/38/39/13/19/"12+1"/"10+1"/"10+2"). El fail-fast
            // de `bot == null` está al inicio del método — acá ya es safe.
            return sectorUniforme.bot;
        },

        /**
         * Valor de display compacto de un sector según el modo de presentación
         * del cero configurado en el sorteo. Equivale a `getDisplayNumber-
         * Compact(num, modo, labels)` del SSOT, con la config explícita.
         *
         * Solo afecta a los sectores de cero — un sector normal (1..N) se
         * devuelve tal cual. Por "compacto": en POR_TEXTO devuelve solo el
         * número corto (`raw.bot`), no la versión concatenada de dos líneas.
         */
        obtenerDisplayDeSectorCompacto(sector, modo = ZERO_DISPLAY_MODES.POR_NUMERO, labels) {
            // Migrado a `sectoresUniformesDeLaRueda` (§8.6) — mismo patrón
            // que `obtenerDisplayDeSector`. Diferencia: en POR_TEXTO
            // devuelve solo el número corto (`bot`), no la concatenación
            // top+bot de dos líneas. Para celdas donde no cabe el texto
            // completo (history-bar compacto, tickets impresos).
            const sectorUniforme = sectorUniformePorValor.get(sector);
            if (!sectorUniforme || sectorUniforme.esCero !== true) return sector;
            // Fail-fast (§2A) — espejo de `obtenerDisplayDeSector`.
            if (sectorUniforme.bot == null) {
                throw new Error(
                    `[ConfiguracionDeRueda] Sector ${sector} de la rueda ` +
                        `"${configDeRueda.id}" está marcado esCero=true pero no declara ` +
                        '`bot` (displayValue). Receta corrupta — un sector de cero sin valor ' +
                        'de display rompería tickets/UI.'
                );
            }
            const indiceDelCero = indiceDeCeroEntreSectoresDeCeroPorValor.get(sector);

            if (modo === ZERO_DISPLAY_MODES.POR_CEROS) {
                return '0'.repeat(indiceDelCero + 1);
            }
            if (modo === ZERO_DISPLAY_MODES.POR_TEXTO) {
                const override = Array.isArray(labels) ? labels[indiceDelCero] : null;
                if (override && typeof override === 'object' && !Array.isArray(override)) {
                    const numeroCorto = override.bot != null ? String(override.bot).trim() : '';
                    if (numeroCorto) return numeroCorto;
                }
                // del sector uniforme es el único número corto disponible.
                return sectorUniforme.bot;
            }
            // POR_NUMERO (default): `bot` del sector uniforme.
            return sectorUniforme.bot;
        },
    };

    Object.freeze(configuracionDeRueda);
    return configuracionDeRueda;
}
